import { HTMLAttributes, forwardRef, ReactNode } from 'react';
import './EmptyState.css';

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  /** Icon or illustration to display */
  icon?: ReactNode;
  /** Title text */
  title: string;
  /** Description text */
  description?: string;
  /** Action buttons or links */
  action?: ReactNode;
}

/**
 * EmptyState component for displaying when no data is available.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="No items found"
 *   description="Get started by creating your first item."
 *   action={<Button variant="primary">Create Item</Button>}
 * />
 * ```
 */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ icon, title, description, action, className = '', ...props }, ref) => {
    const classNames = ['ui-empty-state', className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {icon && <div className="ui-empty-state__icon">{icon}</div>}
        <h3 className="ui-empty-state__title">{title}</h3>
        {description && (
          <p className="ui-empty-state__description">{description}</p>
        )}
        {action && <div className="ui-empty-state__action">{action}</div>}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';
